import { useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Order } from '@alkor/types';
import { api } from '@/lib/api';
import { useShop } from '@/state';
import { track } from '@/lib/analytics';
import { formatDateHuman, formatPrice, pluralize, DELIVERY_LABEL } from '@alkor/utils';
import { IconBox, IconCheck, IconTelegram } from '@/components/icons';
import { EmptyState, Notice, OrderStatusChip, TileSkeleton, ErrorState } from '@/components/ui';
import { MainButton } from '@/components/MainButton';
import { isNetworkError, isPaused } from '@/lib/online';

/* ── Экран «Заказ принят» ───────────────────────────────────────────────── */

export function OrderSuccessScreen() {
  const { number = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const fromState = (location.state as { order?: Order } | null)?.order;

  const query = useQuery({
    queryKey: ['order', number],
    queryFn: () => api.order(number),
    initialData: fromState,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data) track('order_created', { props: { orderNumber: query.data.orderNumber } });
  }, [query.data]);

  if (isPaused(query)) return <ErrorState offline onRetry={() => void query.refetch()} />;
  if (query.isLoading) return <div className="block" style={{ marginTop: 24 }}><TileSkeleton count={2} /></div>;
  if (query.isError || !query.data) {
    return (
      <ErrorState
        message="Не удалось загрузить заказ"
        offline={isNetworkError(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const order = query.data;

  return (
    <>
      <div className="success">
        <div className="success__ring"><IconCheck size={34} /></div>
        <div className="eyebrow">Заказ принят</div>
        <div className="success__num">{order.orderNumber}</div>
        <p style={{ color: 'var(--c-text-body)', fontSize: 14.5, lineHeight: 1.55, maxWidth: '30ch', margin: '0 auto' }}>
          Менеджер свяжется в рабочее время и подтвердит наличие и сроки.
        </p>
      </div>

      <div className="summary" style={{ marginTop: 8 }}>
        <div className="srow">
          <span>Позиций</span>
          <span style={{ fontFamily: 'var(--f-mono)' }}>{order.items.length}</span>
        </div>
        <div className="srow">
          <span>Способ получения</span>
          <span>{DELIVERY_LABEL[order.delivery]}</span>
        </div>
        <div className="srow srow--total">
          <span>Сумма</span>
          <span className="price">{formatPrice(order.total)}</span>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Notice>Подтверждение отправлено в чат с ботом — оно останется в переписке.</Notice>
      </div>

      <div className="block" style={{ marginTop: 18 }}>
        <button type="button" className="secbtn" onClick={() => navigate('/orders')}>
          <IconBox size={20} />
          Мои заказы
        </button>
      </div>

      <div className="pad" />
      <MainButton text="Вернуться в каталог" onClick={() => navigate('/')} />
    </>
  );
}

/* ── Список заказов ─────────────────────────────────────────────────────── */

export function OrdersScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { showToast } = useShop();

  const query = useQuery({ queryKey: ['orders'], queryFn: api.orders, staleTime: 30_000 });

  const repeat = async (order: Order) => {
    try {
      const res = await api.repeatOrder(order.orderNumber);
      await qc.invalidateQueries({ queryKey: ['cart'] });
      if (res.unavailable.length === 0) {
        showToast(`Добавлено позиций: ${res.added.length}`);
      } else {
        // Честный ответ вместо тихого пропуска недоступных позиций
        showToast(
          `${res.added.length} из ${res.added.length + res.unavailable.length} позиций добавлены. ` +
          `Недоступно: ${res.unavailable.join(', ')}`,
        );
      }
      navigate('/cart');
    } catch {
      showToast('Не удалось повторить заказ');
    }
  };

  if (isPaused(query)) return <ErrorState offline onRetry={() => void query.refetch()} />;
  if (query.isLoading) return <div className="block" style={{ marginTop: 20 }}><TileSkeleton count={3} /></div>;
  if (query.isError) {
    return (
      <ErrorState
        message={(query.error as Error).message}
        offline={isNetworkError(query.error)}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const orders = query.data ?? [];

  if (orders.length === 0) {
    return (
      <EmptyState
        icon={<IconBox size={24} />}
        title="Заказов пока нет"
        text="Оформленные заказы появятся здесь: номер, состав, статус и кнопка повторить."
        action={<button type="button" className="secbtn" onClick={() => navigate('/')}>Перейти в каталог</button>}
      />
    );
  }

  return (
    <>
      <div className="screen-title">Мои заказы</div>
      <div className="block" style={{ marginTop: 14 }}>
        <div className="plist">
          {orders.map((o) => (
            <button
              type="button"
              className="ordrow"
              key={o.orderNumber}
              onClick={() => navigate(`/orders/${o.orderNumber}`)}
            >
              <div className="ordrow__l1">
                <span className="ordrow__num">{o.orderNumber}</span>
                <OrderStatusChip status={o.status} />
              </div>
              <div className="ordrow__items">{o.items.map((i) => i.productName).join(', ')}</div>
              <div className="ordrow__l2">
                <span className="ordrow__date">
                  {formatDateHuman(o.createdAt)} · {pluralize(o.items.length, 'позиция', 'позиции', 'позиций')}
                </span>
                <span className="price">{formatPrice(o.total)}</span>
              </div>
              {(o.status === 'completed' || o.status === 'cancelled') && (
                <span
                  role="button"
                  tabIndex={0}
                  className="secbtn"
                  style={{ marginTop: 12 }}
                  onClick={(e) => { e.stopPropagation(); void repeat(o); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); void repeat(o); }
                  }}
                >
                  Повторить заказ
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="block" style={{ marginTop: 16 }}>
        <Notice>
          <IconTelegram size={14} /> Об изменении статуса заказа бот напишет в чат.
        </Notice>
      </div>

      <div className="pad" />
    </>
  );
}
